import type { Paragraph } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  DEFAULT_ALIGNMENT,
  DEFAULT_FONT_FAMILY_ID,
  DEFAULT_FONT_ID,
  DEFAULT_POINT_SIZE,
} from '@/lib/text/constants/text-defaults'
import type { NewShapeTextSeedStyle } from '@/lib/shapes/types/new-shape-text-seed-style'

/**
 * Shapes that support embedded text editing. Includes closed pen-drawn paths.
 *
 * Excludes kindId 4 (legacy image kind) and photo-filled shapes — a photo container's fill and
 * shape text are mutually exclusive on the wire (`buildShapeContent`), so text can never be
 * entered or edited on a placed photo, including via the Type tool.
 */
export function isShapeTextSupported(shape: ShapeData): boolean {
  if (shape.kindId === 4 || shape.photoFill != null) return false

  if (shape.type === 'path') {
    // Only closed pen-drawn paths have an interior that can hold text
    return typeof shape.path === 'string' && /[Zz]\s*$/.test(shape.path.trim())
  }

  const { kindId } = shape

  if (kindId == null) return true
  return kindId !== 7
}

/**
 * True when a shape is acting as a text frame.
 *
 * A shape "is a text frame" purely by carrying `paragraphs` — there is no dedicated flag
 * (see `buildEditorShape` in `stowed-element-builder.ts`). **Presence, not content, is the test:**
 * an empty converted frame is still a frame. Gating this on `hasShapeTextContent` is the bug behind
 * both WMPCM-8160 (empty frames reloaded as plain shapes) and WMPCM-8969 (double-clicking a
 * text-cleared shape dropped into Edit Path instead of re-entering text edit).
 *
 * Excluded:
 * - shapes the shape-text engine cannot lay out at all (`isShapeTextSupported` — open pen paths,
 *   kindId 7 groups, kindId 4 legacy images, and photo-filled shapes)
 */
export function isShapeTextFrame(shape: ShapeData): boolean {
  if (shape.paragraphs == null) return false

  return isShapeTextSupported(shape)
}

/**
 * `seed` is supplied only at the point a shape is first turned into a text frame. Every other call
 * site is a read-time fallback for a frame that already exists and carries its own style, so those
 * pass nothing and keep the hardcoded defaults.
 */
export function defaultShapeTextParagraphs(seed?: NewShapeTextSeedStyle): Paragraph[] {
  return [
    {
      alignment: seed?.alignment ?? DEFAULT_ALIGNMENT,
      textLines: [
        {
          textRuns: [
            {
              textContent: '',
              fontFamilyId: seed?.fontFamilyId ?? DEFAULT_FONT_FAMILY_ID,
              fontId: seed?.fontId ?? DEFAULT_FONT_ID,
              pointSize: seed?.pointSize ?? DEFAULT_POINT_SIZE,
              ...(seed?.kerning != null ? { kerning: seed.kerning } : {}),
              ...(seed?.tracking != null ? { tracking: seed.tracking } : {}),
              ...(seed?.underline != null ? { underline: seed.underline } : {}),
            },
          ],
        },
      ],
    },
  ]
}

export function hasShapeTextContent(paragraphs: Paragraph[] | undefined): boolean {
  if (!paragraphs?.length) return false

  for (const p of paragraphs) {
    for (const line of p.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        if ((run.textContent ?? '').trim().length > 0) return true
      }
    }
  }

  return false
}
